import { Separator, Stack } from "@fluentui/react";
import { Button, Divider, Spinner, Text } from "@fluentui/react-components";
import {
  ArrowUpload24Regular,
  Bot24Regular,
  Chat24Regular,
  ChatMultiple24Regular,
  ChevronDoubleRightRegular,
  Document16Regular,
  DocumentMultiple24Regular,
  Folder24Regular,
  Lightbulb24Regular,
  People24Regular,
  PlugConnected24Regular,
  PuzzlePiece24Regular,
  Question24Regular,
} from "@fluentui/react-icons";
import { downloadListOfUsersThatInteractedWithOurApplication } from "api";
import { useSharedAdminContentHeaderStyles } from "theme/sharedStyles";
import { findSidebarItem } from "components/adminSidebar/adminSidebarConfig";
import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store";
import {
  setGapBetweenHeadersAndDetailsList,
  useStyles,
} from "./ManageStatistics.styles";
import { DateRangeSelector } from "./components/DateRangeSelector";
import { MetricCard, MetricCardGrid } from "./components/MetricCard";
import { ChartCard } from "./components/StatisticsCharts";
import { ChartExportToCSV } from "./exporter/ChartExportToCSV";
import { useStatisticsData } from "./hooks/useStatisticsData";
import { tickFormatter } from "./utils/chartUtils";
import { getStartAndEndOfPeriod } from "./utils/dateUtils";

export const ManageStatistics = (): JSX.Element => {
  const pageTitle = findSidebarItem("statistics")?.label ?? "Statistics";
  const headerClasses = useSharedAdminContentHeaderStyles();
  const styles = useStyles();
  const [dateRangeType, setDateRangeType] = useState<string>("Day");
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>(
    undefined,
  );
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>(
    undefined,
  );
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [customDateError, setCustomDateError] = useState<string | null>(null);

  const useCases = useSelector((state: RootState) => state.useCasesSlice.value);
  const statisticsData = useStatisticsData(useCases);

  const handleDateChange = (date: Date, rangeType: string): void => {
    setSelectedDate(date);
    const { startDate, endDate } = getStartAndEndOfPeriod(date, rangeType);
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const handleRangeTypeChange = (newRangeType: string) => {
    setDateRangeType(newRangeType);
    if (selectedDate && newRangeType !== "Custom") {
      const { startDate, endDate } = getStartAndEndOfPeriod(
        selectedDate,
        newRangeType,
      );
      setStartDate(startDate);
      setEndDate(endDate);
    }
  };

  const handleLoadStatistics = async () => {
    if (startDate && endDate) {
      await statisticsData.fetchAllStatistics(startDate, endDate);
    }
  };

  const handleCustomDateChange = () => {
    if (customStartDate && customEndDate && customStartDate < customEndDate) {
      const adjustedStartDate = new Date(customStartDate);
      adjustedStartDate.setHours(0, 0, 0, 0);

      const adjustedEndDate = new Date(customEndDate);
      adjustedEndDate.setHours(23, 59, 59, 999);

      setStartDate(adjustedStartDate);
      setEndDate(adjustedEndDate);
      setCustomDateError(null);
    } else {
      setCustomDateError("Start date must be earlier than end date.");
    }
  };

  const formatDateRange = () => {
    if (!startDate || !endDate) return "";
    return `${startDate.toISOString().substring(0, 10)} - ${endDate.toISOString().substring(0, 10)}`;
  };

  const userActivityChartData = {
    chartTitle: "User Accesses within Timeframe",
    lineChartData: [
      {
        legend: "Unique users per day",
        data: statisticsData.activityBinData,
      },
    ],
  };

  const modelUsageChartData = {
    chartTitle: "Model usages within Timeframe",
    lineChartData: statisticsData.modelUsageBinData,
  };

  const copiedConversationChartData = {
    chartTitle: "CopiedModels within Timeframe",
    lineChartData: [
      {
        legend: "Copied Chats per day",
        data: statisticsData.copiedConversationsBinData,
      },
    ],
  };

  const fileUploadChartData = {
    chartTitle: "FileUploads within Timeframe",
    lineChartData: [
      {
        legend: "File uploads per day",
        data: statisticsData.fileUploadsBinData,
      },
    ],
  };

  const workspacesChartData = {
    chartTitle: "Workspaces created within Timeframe",
    lineChartData: [
      {
        legend: "Workspaces created per day",
        data: statisticsData.workspacesUsageBinData,
      },
    ],
  };

  const nhqaChartData = {
    chartTitle: "Uses of NHQA within Timeframe",
    lineChartData: [
      {
        legend: "NHQA per day",
        data: statisticsData.nhqaUsageBinData,
      },
    ],
  };

  const useCasePromptExportData = {
    chartTitle: "Use Case Prompt Usage",
    lineChartData: statisticsData.groupedPromptUsage.map((group) => ({
      legend: group.useCaseTitle,
      data: group.prompts.map((prompt) => ({
        x: prompt.promptTitle,
        y: prompt.count,
      })),
    })),
  };

  const overallMetricsForExport = {
    chartTitle: "Metrics",
    lineChartData: [
      {
        legend: "Workspaces Created",
        data: [{ x: formatDateRange(), y: statisticsData.workspacesCount }],
      },
      {
        legend: "NHQA Uses",
        data: [{ x: formatDateRange(), y: statisticsData.nhqaCount }],
      },
      {
        legend: "AI Model Usage",
        data:
          statisticsData.modelUsageCounts?.map((model) => ({
            x: formatDateRange(),
            y: `${model._id}: ${model.count}`,
          })) || [],
      },
      {
        legend: "User Activity",
        data: [{ x: formatDateRange(), y: statisticsData.userActivityCount }],
      },
      {
        legend: "Copied Chats",
        data: [
          { x: formatDateRange(), y: statisticsData.copiedConversationsCount },
        ],
      },
      {
        legend: "File Uploads",
        data: [{ x: formatDateRange(), y: statisticsData.fileUploadCount }],
      },
      {
        legend: "Knowledge Components Usage",
        data: statisticsData.kgcounts.map((model) => ({
          x: formatDateRange(),
          y: `${model._id}: ${model.count}`,
        })),
      },
      {
        legend: "Workspace Library Documents",
        data: [
          {
            x: formatDateRange(),
            y: statisticsData.workspaceLibraryDocumentsCount,
          },
        ],
      },
    ],
  };

  const mcpChartData = {
    chartTitle: "MCP Usage within Timeframe",
    lineChartData: [
      {
        legend: "MCP calls per day",
        data: statisticsData.mcpUsageBinData,
      },
    ],
  };

  const hasChartData =
    statisticsData.activityBinData.length > 0 ||
    statisticsData.modelUsageBinData.length > 0 ||
    statisticsData.copiedConversationsBinData.length > 0 ||
    statisticsData.fileUploadsBinData.length > 0 ||
    statisticsData.nhqaUsageBinData.length > 0 ||
    statisticsData.workspacesUsageBinData.length > 0 ||
    statisticsData.workspaceLibraryDocumentsBinData.length > 0 ||
    statisticsData.mcpUsageBinData.length > 0;

  return (
    <>
      <div className={headerClasses.root}>
        <div className={headerClasses.titleRow}>
          <h1 className={headerClasses.title}>{pageTitle}</h1>
        </div>
      </div>

      <Stack
        tokens={setGapBetweenHeadersAndDetailsList}
        className={styles.container}
        verticalAlign="start"
        horizontalAlign="start"
      >
        <div className={styles.miniContainer}>
          <DateRangeSelector
            dateRangeType={dateRangeType}
            selectedDate={selectedDate}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            customDateError={customDateError}
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
            onRangeTypeChange={handleRangeTypeChange}
            onCustomStartDateChange={setCustomStartDate}
            onCustomEndDateChange={setCustomEndDate}
            onCustomDateApply={handleCustomDateChange}
          />

          <Separator />

          <Button
            appearance="primary"
            onClick={handleLoadStatistics}
            disabled={!startDate || !endDate || statisticsData.isLoading}
            className={styles.loadStatisticsButton}
          >
            {statisticsData.isLoading ? "Loading..." : "Load Statistics"}
          </Button>
        </div>

        <Separator />

        <div className={styles.exportAndStatusContainer}>
          <ChartExportToCSV
            lineChartData={overallMetricsForExport}
            columns={["Date", "UserActivity", "Metric"]}
          />
          {statisticsData.isLoading && (
            <div className={styles.loadingIndicator}>
              <Spinner size="tiny" />
              <Text size={200}>Loading data...</Text>
            </div>
          )}
        </div>

        <div className={styles.cardContainer}>
          <MetricCard
            icon={<People24Regular />}
            title="User Activity"
            tooltip="This shows the count of unique users within the selected period."
            value={statisticsData.userActivityCount}
            isLoading={statisticsData.isLoading}
          >
            <Divider />
            {startDate && endDate && (
              <Button
                className={styles.downloadActiveUsers}
                icon={<Document16Regular />}
                onClick={() =>
                  downloadListOfUsersThatInteractedWithOurApplication({
                    startDate,
                    endDate,
                  })
                }
              >
                <Text size={100} style={{ fontWeight: "bold" }}>
                  Download Active Users ids
                </Text>
              </Button>
            )}
          </MetricCard>

          <MetricCard
            icon={<Bot24Regular />}
            title="AI Model Usage"
            tooltip="This shows the usage count of AI models within the selected period."
            value={undefined}
            isLoading={statisticsData.isLoading}
          >
            {statisticsData.modelUsageCounts.length > 0 ? (
              <Stack tokens={{ childrenGap: 4 }}>
                {statisticsData.modelUsageCounts.map((model) => (
                  <Text key={model._id} size={300}>
                    {model._id}: {model.count} uses
                  </Text>
                ))}
              </Stack>
            ) : (
              <Text size={300}>No data available</Text>
            )}
          </MetricCard>

          <MetricCard
            icon={<ChatMultiple24Regular />}
            title="Copied Chats"
            tooltip="This shows how many chats were copied within the selected period."
            value={statisticsData.copiedConversationsCount}
            isLoading={statisticsData.isLoading}
          />

          <MetricCard
            icon={<ArrowUpload24Regular />}
            title="File Uploads"
            tooltip="This shows how many files were uploaded within the selected period."
            value={statisticsData.fileUploadCount}
            isLoading={statisticsData.isLoading}
          />

          <MetricCard
            icon={<Question24Regular />}
            title="NHQA Uses"
            tooltip="This shows how many times nhqa was used during the selected period."
            value={statisticsData.nhqaCount}
            isLoading={statisticsData.isLoading}
          />

          <MetricCard
            icon={<Folder24Regular />}
            title="Workspaces Created"
            tooltip="This shows how many times workspaces were created within the selected period."
            value={statisticsData.workspacesCount}
            isLoading={statisticsData.isLoading}
          />

          <MetricCard
            icon={<DocumentMultiple24Regular />}
            title="Workspace Library Documents"
            tooltip="This shows how many documents were loaded into Workspace Libraries within the selected period."
            value={statisticsData.workspaceLibraryDocumentsCount}
            isLoading={statisticsData.isLoading}
          />

          <MetricCard
            icon={<PlugConnected24Regular />}
            title="MCP Total Usage"
            tooltip="This shows the total number of MCP tool calls within the selected period."
            value={statisticsData.mcpTotalUsage}
            isLoading={statisticsData.isLoading}
          />

          <MetricCard
            icon={<People24Regular />}
            title="MCP Unique Users"
            tooltip="This shows the number of unique users who used MCP tools within the selected period."
            value={statisticsData.mcpUniqueUsers}
            isLoading={statisticsData.isLoading}
          />

          <MetricCardGrid
            icon={<Chat24Regular />}
            title="Temporary Chat Clicked"
            tooltip="How many times users clicked the Temporary Chat button."
            isLoading={statisticsData.isLoading}
          >
            <Text size={300}>
              {statisticsData.tempChatCount !== undefined ? (
                statisticsData.tempChatCount
              ) : (
                <Text size={300}>No data available</Text>
              )}
            </Text>
          </MetricCardGrid>

          <MetricCardGrid
            icon={<ChevronDoubleRightRegular />}
            title="Chats Clicked"
            tooltip="How many times users clicked the saved chats."
            isLoading={statisticsData.isLoading}
          >
            <Text size={300}>
              {statisticsData.conversationClicks !== undefined ? (
                statisticsData.conversationClicks
              ) : (
                <Text size={300}>No data available</Text>
              )}
            </Text>
          </MetricCardGrid>
        </div>

        <MetricCardGrid
          icon={<PlugConnected24Regular />}
          title="MCP Usage Breakdown"
          tooltip="This shows usage counts per MCP event type within the selected period."
          isLoading={statisticsData.isLoading}
        >
          {statisticsData.mcpUsageSummary.length > 0 ? (
            <Stack tokens={{ childrenGap: 4 }}>
              {statisticsData.mcpUsageSummary.map((item) => (
                <Text key={item._id} size={300}>
                  {item._id}: {item.count}
                </Text>
              ))}
            </Stack>
          ) : (
            <Text size={300}>No MCP usage data available</Text>
          )}
        </MetricCardGrid>

        <MetricCardGrid
          icon={<PuzzlePiece24Regular />}
          title="Knowledge Components Usage"
          tooltip="This shows the usage of knowledge components within the selected period."
          isLoading={statisticsData.isLoading}
        >
          {statisticsData.kgcounts.length > 0 ? (
            statisticsData.kgcounts.map((model) => (
              <Text key={model._id} size={300} style={{ maxWidth: "350px" }}>
                {model._id}: {model.count}
              </Text>
            ))
          ) : (
            <Text size={300}>No data available</Text>
          )}
        </MetricCardGrid>

        <MetricCardGrid
          icon={<Lightbulb24Regular />}
          title="Use Case Prompt Usage"
          tooltip="This shows how many times prompts tied to use cases were used."
          isLoading={statisticsData.isLoading}
        >
          {statisticsData.groupedPromptUsage.length > 0 ? (
            <>
              <ChartExportToCSV
                lineChartData={useCasePromptExportData}
                columns={["Prompt", "UsageCount", "UseCase"]}
              />
              {statisticsData.groupedPromptUsage.map((group) => (
                <div key={group.useCaseTitle}>
                  <Text weight="semibold">{group.useCaseTitle}</Text>
                  <div style={{ paddingLeft: "8px" }}>
                    {group.prompts.map((prompt, index) => (
                      <div key={index}>
                        <Text>
                          - {prompt.promptTitle}: {prompt.count}
                        </Text>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <Text size={300}>No prompt usage available</Text>
          )}
        </MetricCardGrid>

        <div className={styles.chartsContainer}>
          {statisticsData.isLoading || hasChartData ? (
            <>
              <div className={styles.chartsRow}>
                {(statisticsData.isLoading ||
                  statisticsData.activityBinData.length > 0) && (
                  <ChartCard
                    chartTitle={userActivityChartData.chartTitle}
                    lineChartData={userActivityChartData.lineChartData}
                    yMinValue={0}
                    yMaxValue={statisticsData.activityMax}
                    legendsOverflowText="users..."
                    exportColumns={["Date", "UserActivity", "Metric"]}
                    isLoading={statisticsData.isLoading}
                  />
                )}

                {(statisticsData.isLoading ||
                  statisticsData.modelUsageBinData.length > 0) && (
                  <ChartCard
                    chartTitle={modelUsageChartData.chartTitle}
                    lineChartData={modelUsageChartData.lineChartData}
                    yMinValue={statisticsData.modelUsageMin}
                    yMaxValue={statisticsData.modelUsageMax}
                    legendsOverflowText="models..."
                    exportColumns={["Date", "ModelUsage", "Model"]}
                    isLoading={statisticsData.isLoading}
                  />
                )}
              </div>

              <div className={styles.chartsRowWithTopPadding}>
                {(statisticsData.isLoading ||
                  statisticsData.copiedConversationsBinData.length > 0) && (
                  <ChartCard
                    chartTitle={copiedConversationChartData.chartTitle}
                    lineChartData={copiedConversationChartData.lineChartData}
                    yMaxValue={Math.ceil(statisticsData.copiedConversationsMax)}
                    legendsOverflowText="copied..."
                    exportColumns={[
                      "Date",
                      "CopiedConversationCount",
                      "Metric",
                    ]}
                    yAxisTickFormat={tickFormatter}
                    isLoading={statisticsData.isLoading}
                  />
                )}

                {(statisticsData.isLoading ||
                  statisticsData.fileUploadsBinData.length > 0) && (
                  <ChartCard
                    chartTitle={fileUploadChartData.chartTitle}
                    lineChartData={fileUploadChartData.lineChartData}
                    yMaxValue={statisticsData.fileUploadsMax}
                    legendsOverflowText="uploads..."
                    exportColumns={["Date", "FileUploadsCount", "Metric"]}
                    isLoading={statisticsData.isLoading}
                  />
                )}
              </div>

              <div className={styles.cardContainer}>
                {(statisticsData.isLoading ||
                  statisticsData.nhqaUsageBinData.length > 0) && (
                  <ChartCard
                    chartTitle={nhqaChartData.chartTitle}
                    lineChartData={nhqaChartData.lineChartData}
                    yMaxValue={statisticsData.modelNHQAMax}
                    legendsOverflowText="nhqa..."
                    exportColumns={["Date", "NHQAUsage", "Metric"]}
                    isLoading={statisticsData.isLoading}
                  />
                )}

                {(statisticsData.isLoading ||
                  statisticsData.workspacesUsageBinData.length > 0) && (
                  <ChartCard
                    chartTitle={workspacesChartData.chartTitle}
                    lineChartData={workspacesChartData.lineChartData}
                    yMaxValue={statisticsData.workspacesMax}
                    legendsOverflowText="workspaces created..."
                    exportColumns={["Date", "Workspaces Created", "Metric"]}
                    isLoading={statisticsData.isLoading}
                  />
                )}

                {(statisticsData.isLoading ||
                  statisticsData.workspaceLibraryDocumentsBinData.length >
                    0) && (
                  <ChartCard
                    chartTitle="Workspace Library Documents within Timeframe"
                    lineChartData={[
                      {
                        legend: "Library documents per day",
                        data: statisticsData.workspaceLibraryDocumentsBinData,
                      },
                    ]}
                    yMaxValue={statisticsData.workspaceLibraryDocumentsMax}
                    legendsOverflowText="library docs..."
                    exportColumns={[
                      "Date",
                      "Library Documents Count",
                      "Metric",
                    ]}
                    isLoading={statisticsData.isLoading}
                  />
                )}

                {(statisticsData.isLoading ||
                  statisticsData.mcpUsageBinData.length > 0) && (
                  <ChartCard
                    chartTitle={mcpChartData.chartTitle}
                    lineChartData={mcpChartData.lineChartData}
                    yMaxValue={statisticsData.mcpUsageMax}
                    legendsOverflowText="mcp..."
                    exportColumns={["Date", "MCP Usage Count", "Metric"]}
                    isLoading={statisticsData.isLoading}
                  />
                )}
              </div>
            </>
          ) : (
            <div className={styles.emptyChartsState}>
              <Text size={400}>Please select a date range to chart</Text>
            </div>
          )}
        </div>
      </Stack>
    </>
  );
};
